import React, { useContext } from 'react';
import Button from '../../components/Button';
import CustomModal from '../../components/CustomModal';
import CustomModalHeader from '../../components/CustomModal/CustomModalHeader';
import { EMISSION_STAKE_CONFIRMED, EMISSION_STAKE_PENDING } from '../../config/constants';
import appContext from '../../context/app/appContext';
import errorContext from '../../context/error/errorContext';
import modalContext from '../../context/modal/modalContext';
import { formatUSD } from '../../helpers/formats';
import useClaimableStakingRewards from '../../hooks/web3/vault/useClaimableStakingRewards';
import useUserStakedSpool from '../../hooks/web3/vault/useUserStakedSpool';
import { sleep } from '../../web3/utils';

const StakeEmissionRewardsModal: React.FC = () => {
    const { openModal, closeModal, setModalData } = useContext(modalContext);

    const { claimableStakingRewards, updateClaimableStakingRewards } =
        useClaimableStakingRewards();
    const { userStakedSpool, updateUserStakedSpool } = useUserStakedSpool();

    const { interfaceSDK } = useContext(appContext);
    const { setError } = useContext(errorContext);

    const onClose = () => closeModal('stakeEmissionRewardsModal');

    const compound = async () => {
        try {
            if (!interfaceSDK || !userStakedSpool) return;

            setModalData((prev) => ({ ...prev, message: EMISSION_STAKE_PENDING }));
            openModal('waitingModal');

            const stakingContract =
                await interfaceSDK.get.getSpoolStakingContractInstance();

            const rewards = await stakingContract.compound(true);

            const receipt = await rewards.wait();

            await sleep(3000);

            await updateUserStakedSpool(userStakedSpool);
            await updateClaimableStakingRewards(claimableStakingRewards);

            onClose();
            setModalData((prev) => ({
                ...prev,
                status: EMISSION_STAKE_CONFIRMED,
                txHash: receipt.transactionHash,
                message: '',
            }));
            openModal('successModal');
        } catch (error) {
            setError(error);
        } finally {
            closeModal('waitingModal');
        }
    };

    return (
        <CustomModal
            modalName='stakeEmissionRewardsModal'
            className='stake-emission-rewards-modal'
        >
            <CustomModalHeader title='Stake Emission Rewards' onClose={onClose} />
            <div className='note'>
                Claims both $SPOOL and voSPOOL Emission rewards and stakes them for you in
                a single transaction.
            </div>
            <div className='details'>
                <span>Details</span>
                {claimableStakingRewards &&
                Object.keys(claimableStakingRewards).length ? (
                    <>
                        <div className='detail-container'>
                            <div className='name'>Emission Rewards</div>
                            <div className='value'>
                                {formatUSD(
                                    claimableStakingRewards.spoolRewards['SPOOL'] || 0,
                                )}{' '}
                                $SPOOL
                            </div>
                        </div>
                        <div className='detail-container'>
                            <div className='name'>voSPOOL Emission Rewards</div>
                            <div className='value'>
                                {formatUSD(
                                    claimableStakingRewards.voSpoolRewards['SPOOL'] || 0,
                                )}{' '}
                                $SPOOL
                            </div>
                        </div>
                    </>
                ) : null}
            </div>
            <div className='total'>
                <div className='text'>Total Available to Stake:</div>
                <div className='available'>
                    <div className='claimable'>
                        {claimableStakingRewards &&
                        Object.keys(claimableStakingRewards).length
                            ? formatUSD(
                                  (claimableStakingRewards?.spoolRewards['SPOOL'] ?? 0) +
                                      (claimableStakingRewards?.voSpoolRewards['SPOOL'] ??
                                          0) || 0,
                              )
                            : null}{' '}
                        $SPOOL
                    </div>
                </div>
            </div>
            <Button onClick={compound}>Claim and Stake</Button>
        </CustomModal>
    );
};

export default StakeEmissionRewardsModal;
